import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { AssetTimeseriesProperty } from '@archimed/models';
import {
  ButtonComponent,
  CheckboxComponent,
  ComboboxComponent,
  ComboboxInputDirective,
  ComboboxSingleItemComponent,
  DatepickerComponent,
  DatepickerInputDirective,
  FormFieldComponent,
  FormFieldItemDirective,
  FormFieldLabelDirective,
  IconButtonComponent,
  IconComponent,
  RadioComponent,
  RadioInputDirective,
  RadioLabelDirective,
} from '@totalenergiescode/tds-angular';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import { TIMESERIES_REPOSITORY } from '../../../application/ports/timeseries-repository.port';
import { Asset } from '../../../domain/models/asset.model';
import {
  InterpolationFrequency,
  InterpolationStrategy,
  TimeseriesDataPoint,
  TimeseriesQueryParams,
} from '../../../domain/models/timeseries.model';
import { TimeseriesDataTableComponent } from '../timeseries-panel/timeseries-data-table/timeseries-data-table.component';

type DisplayMode = 'table' | 'chart';

function getDefaultDateFrom(): string {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
}

function getDefaultDateTo(): string {
  return new Date().toISOString().split('T')[0];
}

@Component({
  selector: 'lib-timeseries-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CheckboxComponent,
    ComboboxComponent,
    ComboboxInputDirective,
    ComboboxSingleItemComponent,
    DatepickerComponent,
    DatepickerInputDirective,
    FormFieldComponent,
    FormFieldItemDirective,
    FormFieldLabelDirective,
    IconButtonComponent,
    IconComponent,
    RadioComponent,
    RadioInputDirective,
    RadioLabelDirective,
    NgxEchartsDirective,
    TimeseriesDataTableComponent,
  ],
  providers: [
    provideEchartsCore({
      echarts: () => import('echarts'),
    }),
  ],
  templateUrl: './timeseries-tab.component.html',
  styleUrl: './timeseries-tab.component.scss',
})
export class TimeseriesTabComponent {
  private readonly repository = inject(TIMESERIES_REPOSITORY);
  private queryAssetId: string | null = null;

  readonly asset = input.required<Asset>();
  readonly timeseriesProperties = input<AssetTimeseriesProperty[]>([]);

  readonly selectedProperty = signal<string | null>(null);
  readonly dateFrom = signal<string>(getDefaultDateFrom());
  readonly dateTo = signal<string>(getDefaultDateTo());
  readonly interpolationEnabled = signal(false);
  readonly interpolationFrequency = signal<InterpolationFrequency>('daily');
  readonly interpolationStrategy = signal<InterpolationStrategy>('linear');
  readonly displayMode = signal<DisplayMode>('table');
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly hasQueried = signal(false);
  readonly rawDataPoints = signal<TimeseriesDataPoint[]>([]);

  readonly dataPoints = computed(() => this.rawDataPoints());

  readonly chartOptions = computed(() => {
    const points = this.dataPoints();
    return {
      xAxis: { type: 'time' },
      yAxis: { type: 'value' },
      series: [
        {
          type: 'line',
          data: points.map((p) => [p.timestamp, p.value]),
          smooth: false,
        },
      ],
      tooltip: { trigger: 'axis' },
      grid: { left: '10%', right: '5%', bottom: '15%' },
    };
  });

  readonly canQuery = computed(() => {
    if (!this.selectedProperty() || this.isLoading()) return false;
    const from = new Date(this.dateFrom());
    const to = new Date(this.dateTo());
    return from < to && !isNaN(from.getTime()) && !isNaN(to.getTime());
  });

  readonly showEmpty = computed(() => {
    return (
      this.hasQueried() &&
      !this.isLoading() &&
      !this.error() &&
      this.rawDataPoints().length === 0
    );
  });

  onPropertySelect(value: unknown): void {
    this.selectedProperty.set((value as string) ?? null);
  }

  async onQuery(): Promise<void> {
    const prop = this.selectedProperty();
    if (!prop) return;

    const assetId = this.asset().id;
    this.queryAssetId = assetId;
    this.isLoading.set(true);
    this.error.set(null);
    this.hasQueried.set(true);

    try {
      const params: TimeseriesQueryParams = {
        obid: assetId,
        propertyName: prop,
        assetType: this.asset().entityType,
        startTime: this.dateFrom(),
        endTime: this.dateTo(),
        ...(this.interpolationEnabled()
          ? {
              interpolationFrequency: this.interpolationFrequency(),
              interpolationStrategy: this.interpolationStrategy(),
            }
          : {}),
      };

      const result = await this.repository.query(params);

      // Guard against stale response if asset changed during fetch
      if (this.queryAssetId !== assetId) return;

      this.rawDataPoints.set(result.dataPoints);
    } catch (e) {
      if (this.queryAssetId !== assetId) return;

      this.error.set(
        e instanceof Error ? e.message : 'Failed to fetch timeseries data',
      );
      this.rawDataPoints.set([]);
    } finally {
      if (this.queryAssetId === assetId) {
        this.isLoading.set(false);
      }
    }
  }
}
